import React, { useEffect, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Portal, Base } from '@brandwatch/axiom-components';

import { animated, useTransition } from 'react-spring';

import SlidePicker from './SlidePicker/SlidePicker.jsx';
import SlidePickerSearch from './SlidePicker/SlidePickerSearch.jsx';

import InteractiveComponent from './InteractiveComponent/InteractiveComponent.jsx';
import InteractiveComponentHeader from './InteractiveComponent/InteractiveComponentHeader.jsx';

import './FullscreenPanel.css';
import { InteractiveComponentContext } from '../context.js';

const FullscreenPanelHeader = ({ children }) => {
  return <Base className="FullscreenPanel__Header">{children}</Base>;
};

FullscreenPanelHeader.propTypes = {
  onRequestClose: PropTypes.func,
  children: PropTypes.node
};

const contentMapping = {
  SlidePicker: {
    header: SlidePickerSearch,
    content: SlidePicker,

    animation: {
      from: { transform: 'translateY(100%)' },
      enter: { transform: 'translateY(0%)' },
      leave: { transform: 'translateY(100%)' }
    }
  },
  InteractiveComponent: {
    header: InteractiveComponentHeader,
    content: InteractiveComponent,
    animation: {
      from: { opacity: 0, transform: 'scale(0.9)' },
      enter: { opacity: 1, transform: 'scale(1)' },
      leave: { opacity: 0, transform: 'scale(0.9)' }
    }
  }
};

const FullscreenPanel = ({ isOpen, onRequestClose, panelProps }) => {
  let animation = {
    from: { transform: 'translateY(100%)' },
    enter: { transform: 'translateY(0%)' },
    leave: { transform: 'translateY(100%)' }
  };

  if (panelProps.customAnimation) {
    animation = contentMapping[panelProps.type].animation;
  }

  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflowY = 'hidden';
    } else {
      document.documentElement.style.overflowY = null;
    }
  }, [isOpen]);

  const transition = useTransition(
    isOpen ? contentMapping[panelProps.type] : null,
    null,
    animation
  );

  const [, setContext] = useContext(InteractiveComponentContext);
  const onRequestCloseWithContext = useCallback(() => {
    setContext(null);
    onRequestClose();
  });

  return (
    <Portal>
      {transition.map(({ item, props, key }) => {
        if (item) {
          const Header = item.header;
          const Content = item.content;
          return (
            <animated.div
              key={key}
              style={props}
              className="FullscreenPanel__container vc-text ax-theme--night"
            >
              <FullscreenPanelHeader>
                <Header
                  onRequestClose={onRequestCloseWithContext}
                  {...panelProps}
                />
              </FullscreenPanelHeader>
              <Content
                onRequestClose={onRequestCloseWithContext}
                styleProps={props}
                {...panelProps}
              />
            </animated.div>
          );
        }
      })}
    </Portal>
  );
};

FullscreenPanel.propTypes = {
  isOpen: PropTypes.bool,
  onRequestClose: PropTypes.func,
  panelProps: PropTypes.object
};

FullscreenPanel.defaultProps = {
  isOpen: false
};

export default FullscreenPanel;
